import React, { useState, useEffect } from "react";
import { TextField, Select, MenuItem, CircularProgress } from "@mui/material";
import { TestRunStatusTypes } from "../../enums/TestrunStatusEnum";
import ModalWrapper from "../Modal/modalWrapper/basicModalWrapper";
import axios from "../../api/axiosInstance";
import TestRunPopups from "../TestRuns/TestRunPopups";
import { useSnackbar } from "../../utils/useSnackbar";

const AddTCToTestRunModal = ({ isOpen, onClose, onError, onAdd, testCaseIds, modulePath }) => {
  const [testRuns, setTestRuns] = useState([]); // State to hold test run options
  const [loading, setLoading] = useState(false); // State for loading spinner
  const [error, setError] = useState(""); // State for error message
  const [popupData, setPopupData] = useState({ show: false, type: null });
  const { showSnackbar } = useSnackbar();
  const DEFAULT_FILTER = {
    "field": "executionStatus",
    "operator": "not",
    "value": "Completed"
  };

  // Fetch test runs when the modal is opened or after creation
  useEffect(() => {
    if (isOpen) {
      fetchTestRuns();
    }
  }, [isOpen]);

  const fetchTestRuns = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/testrun/get`, {
        params: {
          filter: DEFAULT_FILTER,
          //excludeTestcaseIds: testCaseIds
        },
      });

      if (response.status >= 200 && response.status < 300) {
        setTestRuns(response.data); // Assuming response.data is an array of test runs
      } else {
        setError("Failed to fetch test runs");
      }
    } catch (err) {
      setError("Error fetching test runs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAdd = async (data) => {
    try {
        const testRunId = data.testRunId;
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/testrun/addTest`,
        { testRunId, testCaseIds, modulePath },
        {
          validateStatus: (status) => status < 500, // Resolve only if the status code is less than 500
        }
      );

      if (response.status < 200 || response.status >= 300) {
        onError(response.data.message || "Failed to add in test run", "error");
        return;
      }
      onAdd(response.data);
      onClose();
    } catch (error) {
      console.error("Error creating test run:", error);
      onError("Error creating test run");
    }
  };

  // Handle Create Test Run button click
  const handleOpenCreateTestRun = () => {
    setPopupData({
      type: "create",
      show: true,
      closePopup: () => setPopupData({ show: false, type: null }),
      successFunction: ({ type, actionType, message }) => {
        setPopupData({ show: false, type: null });
        showSnackbar(message, actionType);
        fetchTestRuns();
      },
      errorFunction: ({ type, errorType, errorMessage }) => {
        setPopupData({ show: false, type: null });
        onError(errorType, errorMessage);
      },
    });
  };

  const buttons = [
    {
      label: "Cancel",
      variant: "outlined",
      onClick: onClose,
    },
    {
      label: "Add",
      variant: "contained",
      color: "primary",
    },
  ];

  const fields = [
    {
      Component: Select,
      label: 'Test Run',
      name: "testRunId",
      inputLabel: "Test Run",
      required: true,
      fullWidth: true,
      options: testRuns.map((testRun) => ({
        value: testRun.id,
        label: testRun.testCycleName,
      })),
      helperText: error || "",
      error: !!error ,
      isLoading: loading, // Custom prop to show a loading spinner
    },
  ];

  const customButtons = [
    {
      label: "+ Create New",
      variant: "outlined",
      color: "primary",
      size: "small",
      onClick: handleOpenCreateTestRun,
    },
  ];

  return (
    <>
      <ModalWrapper
        open={isOpen}
        onClose={onClose}
        title="Add Test Cases to Test Run"
        fields={fields}
        buttons={buttons}
        customButtons={customButtons}
        onSubmit={handleConfirmAdd}
        containerStyles={{
          // height: "90vh", // Custom height
        }}
        initialValues={{
          testRunId: '',
        }}
      />
      {popupData.show && <TestRunPopups popupData={popupData} />}
    </>
  );
};

export default AddTCToTestRunModal;
